import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface WorkflowSafetyState {
  activeWorkflows: readonly string[];
}

interface WorkflowSafetyActions {
  enter: (name: string) => () => void;
}

interface WorkflowSafetyMeta {
  safe: boolean;
}

interface WorkflowSafetyContextValue {
  state: WorkflowSafetyState;
  actions: WorkflowSafetyActions;
  meta: WorkflowSafetyMeta;
}

const Context = createContext<WorkflowSafetyContextValue | null>(null);

export function WorkflowSafetyProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ReadonlyMap<symbol, string>>(
    () => new Map(),
  );
  const enter = useCallback((name: string) => {
    const token = Symbol(name);
    setActive((current) => new Map(current).set(token, name));
    return () =>
      setActive((current) => {
        const next = new Map(current);
        next.delete(token);
        return next;
      });
  }, []);
  const value = useMemo<WorkflowSafetyContextValue>(
    () => ({
      state: { activeWorkflows: [...active.values()] },
      actions: { enter },
      meta: { safe: active.size === 0 },
    }),
    [active, enter],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useWorkflowSafety() {
  const value = useContext(Context);
  if (!value) throw new Error('Missing workflow safety provider');
  return value;
}

export function WorkflowScope({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  const { enter } = useWorkflowSafety().actions;
  useLayoutEffect(() => enter(name), [enter, name]);
  return children;
}
